import { Link } from "wouter";
import { ArrowLeft, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import { type ReactNode } from "react";

interface LegalSection {
  title: string;
  items: string[];
}

interface LegalLayoutProps {
  title: string;
  intro?: string;
  lastUpdated: string;
  backLabel: string;
  sections: Record<string, LegalSection>;
  dir: "ltr" | "rtl";
  children?: ReactNode;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24, filter: "blur(4px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  },
};

const titleVariants = {
  hidden: { opacity: 0, y: -20, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const lineVariants = {
  hidden: { scaleX: 0, opacity: 0 },
  visible: {
    scaleX: 1,
    opacity: 1,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const bulletVariants = {
  hidden: { opacity: 0, x: -12 },
  visible: (i: number) => ({
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.35,
      delay: i * 0.04,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

export function LegalLayout({ title, intro, lastUpdated, backLabel, sections, dir }: LegalLayoutProps) {
  const isRtl = dir === "rtl";
  const sectionEntries = Object.entries(sections);

  return (
    <div className="min-h-screen bg-background" dir={dir}>
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-1/4 -left-1/4 w-[600px] h-[600px] rounded-full bg-primary/[0.03] blur-[120px] animate-[aurora-drift_22s_ease-in-out_infinite]" />
        <div className="absolute -bottom-1/4 -right-1/4 w-[500px] h-[500px] rounded-full bg-primary/[0.04] blur-[100px] animate-[aurora-drift_28s_ease-in-out_infinite_reverse]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-primary/[0.02] blur-[80px] animate-[aurora-drift_35s_ease-in-out_infinite]" />
      </div>

      <div className="relative max-w-3xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, x: isRtl ? 20 : -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link href="/">
            <span
              className={`group inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer mb-8 ${isRtl ? "flex-row-reverse" : ""}`}
              data-testid="link-back-home"
            >
              <ArrowLeft className={`w-4 h-4 transition-transform group-hover:${isRtl ? "translate-x-1" : "-translate-x-1"} ${isRtl ? "rotate-180" : ""}`} />
              {backLabel}
            </span>
          </Link>
        </motion.div>

        <motion.div
          className="relative rounded-2xl border border-border/30 bg-card/50 backdrop-blur-xl shadow-lg overflow-hidden"
          initial={{ opacity: 0, y: 30, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/[0.02] via-transparent to-primary/[0.01] pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

          <div className="relative p-8 md:p-12">
            <motion.div variants={titleVariants} initial="hidden" animate="visible">
              <h1
                className="text-3xl md:text-4xl font-serif font-bold tracking-tight text-foreground mb-3"
                style={{ fontFamily: "'Playfair Display', serif" }}
                data-testid="text-legal-title"
              >
                {title}
              </h1>

              <div className="flex items-center gap-2 text-xs text-muted-foreground/60 mb-6">
                <Calendar className="w-3.5 h-3.5" />
                <span data-testid="text-last-updated">{lastUpdated}</span>
              </div>

              <motion.div
                className="h-px bg-gradient-to-r from-primary/30 via-primary/10 to-transparent mb-8"
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                style={{ transformOrigin: isRtl ? "right" : "left" }}
              />
            </motion.div>

            {intro && (
              <motion.p
                className="text-base text-muted-foreground leading-relaxed mb-10"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                data-testid="text-legal-intro"
              >
                {intro}
              </motion.p>
            )}

            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="space-y-8"
            >
              {sectionEntries.map(([key, section], sectionIdx) => (
                <motion.div key={key} variants={itemVariants} data-testid={`section-${key}`}>
                  <div className="space-y-4">
                    <h2
                      className="text-xl font-semibold text-foreground tracking-tight"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                      data-testid={`text-section-title-${key}`}
                    >
                      {section.title}
                    </h2>

                    <ul className={`space-y-2.5 ${isRtl ? "pr-1" : "pl-1"}`}>
                      {section.items.map((item, idx) => (
                        <motion.li
                          key={idx}
                          className={`flex items-start gap-3 text-sm text-muted-foreground leading-relaxed ${isRtl ? "flex-row-reverse text-right" : ""}`}
                          custom={idx}
                          variants={bulletVariants}
                          data-testid={`text-item-${key}-${idx}`}
                        >
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary/40 shrink-0" />
                          <span>{item}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {sectionIdx < sectionEntries.length - 1 && (
                    <motion.div
                      className="mt-8 h-px bg-gradient-to-r from-border/40 via-border/20 to-transparent"
                      variants={lineVariants}
                      style={{ transformOrigin: isRtl ? "right" : "left" }}
                    />
                  )}
                </motion.div>
              ))}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
